import axiosInstance from '../lib/axios'
import { API_ENDPOINTS } from '../constants/api'
import type {
  PlatformUsageResponse,
  OrgDetailUsageResponse,
  TeamUsageSummary,
  IncidentAnalyticsResponse,
  DeviceHealthResponse,
  DeviceWithHealth,
  SingleDeviceHealth,
  DeviceIncidentListResponse,
  TaskPerformanceResponse,
  ConsumptionResponse,
  OTARolloutResponse,
} from '../types/api.types'

export const platformAnalyticsService = {
  async getUsage(fromDate: string, toDate: string): Promise<PlatformUsageResponse> {
    const { data } = await axiosInstance.get<PlatformUsageResponse>(
      `${API_ENDPOINTS.PLATFORM_ANALYTICS_USAGE}?from_date=${fromDate}&to_date=${toDate}`,
    )
    return data
  },

  async getOrgUsage(orgId: number, fromDate: string, toDate: string): Promise<OrgDetailUsageResponse> {
    const { data } = await axiosInstance.get<OrgDetailUsageResponse>(
      `${API_ENDPOINTS.PLATFORM_ANALYTICS_ORG_USAGE(orgId)}?from_date=${fromDate}&to_date=${toDate}`,
    )
    return data
  },

  async getTopTeams(
    fromDate: string,
    toDate: string,
    orgId?: number,
    limit = 10,
  ): Promise<TeamUsageSummary[]> {
    const params = new URLSearchParams({ from_date: fromDate, to_date: toDate, limit: String(limit) })
    if (orgId) params.set('org_id', String(orgId))
    const { data } = await axiosInstance.get<TeamUsageSummary[] | { teams: TeamUsageSummary[] }>(
      `${API_ENDPOINTS.PLATFORM_ANALYTICS_TEAMS}?${params.toString()}`,
    )
    // API may return plain array or { teams: [...] }
    return Array.isArray(data) ? data : ((data as { teams?: TeamUsageSummary[] }).teams ?? [])
  },

  async getIncidents(
    fromDate: string,
    toDate: string,
    orgId?: number,
  ): Promise<IncidentAnalyticsResponse> {
    const params = new URLSearchParams({ from_date: fromDate, to_date: toDate })
    if (orgId) params.set('org_id', String(orgId))
    const { data } = await axiosInstance.get<IncidentAnalyticsResponse>(
      `${API_ENDPOINTS.PLATFORM_ANALYTICS_INCIDENTS}?${params.toString()}`,
    )
    return data
  },

  async getDeviceHealth(): Promise<DeviceHealthResponse> {
    const { data } = await axiosInstance.get<DeviceHealthResponse>(
      API_ENDPOINTS.PLATFORM_ANALYTICS_DEVICE_HEALTH,
    )
    return data
  },

  async getOrgDeviceHealth(orgId: number): Promise<DeviceWithHealth[]> {
    const { data } = await axiosInstance.get<{ devices?: DeviceWithHealth[] } | DeviceWithHealth[]>(
      API_ENDPOINTS.PLATFORM_ORG_DEVICE_HEALTH(orgId),
    )
    // Backend returns OrgDeviceHealthResponse { devices: [...], ... } — extract the array
    if (Array.isArray(data)) return data
    return (data as { devices?: DeviceWithHealth[] }).devices ?? []
  },

  async getSingleDeviceHealth(orgId: number, deviceId: number): Promise<SingleDeviceHealth> {
    const { data } = await axiosInstance.get<SingleDeviceHealth>(
      API_ENDPOINTS.PLATFORM_DEVICE_SINGLE_HEALTH(orgId, deviceId),
    )
    return data
  },

  async getDeviceIncidents(params?: {
    from_date?: string
    to_date?: string
    org_id?: number
    severity?: string
    incident_type?: string
    is_resolved?: boolean
    limit?: number
    offset?: number
  }): Promise<DeviceIncidentListResponse> {
    const qs = new URLSearchParams()
    if (params?.from_date) qs.set('from_date', params.from_date)
    if (params?.to_date) qs.set('to_date', params.to_date)
    if (params?.org_id) qs.set('org_id', String(params.org_id))
    if (params?.severity) qs.set('severity', params.severity)
    if (params?.incident_type) qs.set('incident_type', params.incident_type)
    if (params?.is_resolved !== undefined) qs.set('is_resolved', String(params.is_resolved))
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    const { data } = await axiosInstance.get<DeviceIncidentListResponse>(
      `${API_ENDPOINTS.PLATFORM_ANALYTICS_DEVICE_INCIDENTS}${qs.toString() ? `?${qs}` : ''}`,
    )
    return data
  },

  async getOTARollout(): Promise<OTARolloutResponse> {
    const { data } = await axiosInstance.get<OTARolloutResponse>(
      API_ENDPOINTS.PLATFORM_ANALYTICS_OTA_ROLLOUT,
    )
    return data
  },

  async getTaskPerformance(fromDate: string, toDate: string, orgId?: number): Promise<TaskPerformanceResponse> {
    const params = new URLSearchParams({ from_date: fromDate, to_date: toDate })
    if (orgId) params.set('org_id', String(orgId))
    const { data } = await axiosInstance.get<TaskPerformanceResponse>(
      `${API_ENDPOINTS.PLATFORM_ANALYTICS_TASK_PERFORMANCE}?${params.toString()}`,
    )
    return data
  },

  async getConsumption(year: number, month: number): Promise<ConsumptionResponse> {
    const { data } = await axiosInstance.get<ConsumptionResponse>(
      `${API_ENDPOINTS.PLATFORM_ANALYTICS_CONSUMPTION}?year=${year}&month=${month}`,
    )
    return data
  },
}
